/**
 * 访问「热度脉搏」—— 营销「热度展示 + 轻等待」的前端数据源。
 *
 * 拉取后端 GET /api/h5/v1/zhcz/pulse，返回：
 *  - mode：运营档位（normal / heat / queue / overload），运行时可切，无需重新构建
 *  - stats：来自匿名埋点的真实热度数字（今日 / 累计 / 近 10 分钟在线 去重访客）
 *
 * 原则：
 *  - 任何失败都降级为 normal + 空数字，绝不阻塞 / 报错影响业务
 *  - 内存缓存 60s + 单飞（in-flight 复用），跨路由复用，避免每页都打后端
 *  - 数字「真实优先」：太少（不可信）时不展示，避免营销翻车
 */
import { useEffect, useState } from 'react'

export type OpsMode = 'normal' | 'heat' | 'queue' | 'overload'

export interface Pulse {
  mode: OpsMode
  stats: { today: number; total: number; online: number }
  ts: number
}

const ENDPOINT = '/api/h5/v1/zhcz/pulse'
const CACHE_TTL_MS = 60 * 1000

export const FALLBACK_PULSE: Pulse = {
  mode: 'normal',
  stats: { today: 0, total: 0, online: 0 },
  ts: 0
}

let memCache: Pulse | null = null
let inflight: Promise<Pulse> | null = null

function isOpsMode(v: unknown): v is OpsMode {
  return v === 'normal' || v === 'heat' || v === 'queue' || v === 'overload'
}

export async function fetchPulse(): Promise<Pulse> {
  if (memCache && Date.now() - memCache.ts < CACHE_TTL_MS) return memCache
  if (inflight) return inflight

  inflight = fetch(ENDPOINT, { credentials: 'omit' })
    .then((r) => (r.ok ? r.json() : null))
    .then((raw: unknown): Pulse => {
      const obj = (raw ?? {}) as Partial<Pulse>
      const s = obj.stats ?? FALLBACK_PULSE.stats
      const pulse: Pulse = {
        mode: isOpsMode(obj.mode) ? obj.mode : 'normal',
        stats: {
          today: Number(s.today) || 0,
          total: Number(s.total) || 0,
          online: Number(s.online) || 0
        },
        ts: Date.now()
      }
      memCache = pulse
      return pulse
    })
    .catch((): Pulse => ({ ...FALLBACK_PULSE, ts: Date.now() }))
    .finally(() => {
      inflight = null
    })

  return inflight
}

/** React hook：组件挂载时拉取一次（共享缓存，不会重复请求） */
export function usePulse(): Pulse | null {
  const [pulse, setPulse] = useState<Pulse | null>(memCache)
  useEffect(() => {
    let alive = true
    fetchPulse().then((p) => {
      if (alive) setPulse(p)
    })
    return () => {
      alive = false
    }
  }, [])
  return pulse
}

// ───────────────────────────────────────────────────────────────
// 社交证明文案：把原始数字包装成「确实很火」但真实的展示
// ───────────────────────────────────────────────────────────────

/** 数据太少则不展示热度徽章（避免「已有 12 位」翻车） */
const MIN_TOTAL_TO_SHOW = 200
/** 今日活跃达到该量级才以「今日」为主口径，否则退回「累计」 */
const MIN_TODAY_AS_PRIMARY = 50
/** 在线人数达到该量级才展示「正在测算」 */
const MIN_ONLINE_TO_SHOW = 5

function withThousands(n: number): string {
  return n.toLocaleString('en-US')
}

/** 累计量向下取整到百，营造「至少这么多」的稳妥口径 */
function roundDownHundred(n: number): number {
  return Math.max(100, Math.floor(n / 100) * 100)
}

export interface HeatCopy {
  /** 主社交证明，如「截止目前已有 1,286 位家长测算」 */
  volume: string
  /** 近 10 分钟完成测算（可选），如「10 分钟内，37 位家长已完成测算」 */
  online?: string
}

/**
 * 2026 中考志愿填报季已收官：热度数字定格为最终静态展示，不再随实时访问变化。
 * 明年重开时把 SEASON_CLOSED 置回 false，即可恢复下方的动态口径。
 */
const SEASON_CLOSED: boolean = true

export function heatCopy(stats: Pulse['stats']): HeatCopy | null {
  if (SEASON_CLOSED) {
    return {
      volume: '截止目前已有 5,486 位家长测算',
      online: '顺利帮助 5,486 位学生完成测算'
    }
  }

  if (!stats || stats.total < MIN_TOTAL_TO_SHOW) return null

  const volume =
    stats.today >= MIN_TODAY_AS_PRIMARY
      ? `截止目前已有 ${withThousands(stats.today)} 位家长测算`
      : `已有 ${withThousands(roundDownHundred(stats.total))}+ 位家长用过`

  const online =
    stats.online >= MIN_ONLINE_TO_SHOW
      ? `10 分钟内，${withThousands(stats.online)} 位家长已完成测算`
      : undefined

  return { volume, online }
}
